import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { useEffect, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import { Iconify } from 'src/components/iconify';
import { usePopover, CustomPopover } from 'src/components/custom-popover';
import useAuth from '../../auth/useAuth';
import { useGetUserQuery } from 'src/generated/graphql';
import {
  getLocalStorageValue,
  selectedOrgStorageKey,
  setLocalStorageValue,
} from 'src/utils/utilsLocalStorage';

// ----------------------------------------------------------------------

export type OrganizationsPopoverProps = ButtonBaseProps & {
  /** Icon only. Used in the collapsed sidebar, where the name does not fit. */
  compact?: boolean;
  data?: {
    id: string;
    name: string;
  }[];
};

export function OrganizationsPopover({ compact = false, sx, ...other }: OrganizationsPopoverProps) {
  const { organizationId, setOrganizationId } = useAuth();

  const [{ data: getUserData }] = useGetUserQuery({});
  const user = getUserData?.getUser;

  const _organizations = useMemo(
    () =>
      (user?.organizations ?? []).map((org) => ({
        id: org.id,
        name: org.name,
      })),
    [user?.organizations],
  );

  const popover = usePopover();

  const handleChangeWorkspace = useCallback(
    (newValue: (typeof _organizations)[0]) => {
      setOrganizationId(newValue.id);
      if (user?.userId) {
        setLocalStorageValue(selectedOrgStorageKey(user.userId), newValue.id);
      }
      popover.onClose();
    },
    [popover, setOrganizationId, user?.userId],
  );

  useEffect(() => {
    if (_organizations.length === 0 || !user?.userId) return;
    const stored = getLocalStorageValue(selectedOrgStorageKey(user.userId), '');
    const preferred =
      stored && _organizations.some((org) => org.id === stored)
        ? stored
        : organizationId != null &&
            _organizations.some((org) => org.id === organizationId)
          ? organizationId
          : _organizations[0].id;
    if (preferred !== organizationId) {
      setOrganizationId(preferred);
    }
    if (preferred !== stored) {
      setLocalStorageValue(selectedOrgStorageKey(user.userId), preferred);
    }
  }, [_organizations, organizationId, setOrganizationId, user?.userId]);

  const selectedOrganization = _organizations.find((org) => org.id === organizationId);

  if (!selectedOrganization) {
    return null;
  }

  return (
    <>
      <ButtonBase
        disableRipple
        aria-label={selectedOrganization.name}
        onClick={popover.onOpen}
        sx={{
          py: 0.5,
          gap: 0.5,
          minWidth: 0,
          maxWidth: '100%',
          justifyContent: 'flex-start',
          ...sx,
        }}
        {...other}
      >
        {!compact && (
          <Box
            component="span"
            sx={{
              typography: 'subtitle2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedOrganization.name}
          </Box>
        )}

        <Iconify
          width={16}
          icon="carbon:chevron-sort"
          sx={{ color: 'text.disabled', flexShrink: 0 }}
        />
      </ButtonBase>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'top-left' } }}
      >
        <MenuList sx={{ width: 240 }}>
          {_organizations.map((option) => (
            <MenuItem
              key={option.id}
              selected={option.id === organizationId}
              onClick={() => handleChangeWorkspace(option)}
              sx={{ height: 48 }}
            >
              <Box component="span" sx={{ flexGrow: 1 }}>
                {option.name}
              </Box>
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}
